import { Button } from "@/components/atoms/Button";
import { Checkbox } from "@/components/atoms/Checkbox";
import { Typography } from "@/components/atoms/Typography";
import {
    CalendarIcon,
    DirhamIcon,
    FunnelIcon,
    TimerIconBold,
    TriangleAlertIcon,
} from "@/components/atoms/icons";
import { PaymentItemMobile } from "@/components/molecules/PaymentItem";
import { PaymentsEmptyState } from "@/components/molecules/PaymentsEmptyState";
import { TextHeader } from "@/components/molecules/header/TextHeader";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { DT_SCREEN } from "@/core/constants/dynatraceScreens";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { trackDynatraceEvent } from "@/core/services/dynatraceService";
import { trackCatalogEvent } from "@/core/services/trackDynatraceCatalog";
import { useAppStore } from "@/core/stores/appStore";
import { usePaymentFilterStore } from "@/core/stores/paymentFilterStore";
import {
    formatDueDate,
    formatDueDateWithPrefix,
    getAllOverdueInstallments,
    getAllUpcomingDueInstallments,
    getUpcomingTabBadgeInfo,
    mapInstallmentStatusToPaymentStatus,
} from "@/modules/payments/utils/installmentUtils";
import { router, useLocalSearchParams } from "expo-router";
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type TabType = "upcoming" | "history";

const MONTH_KEYS = [
  "common.monthJanuary",
  "common.monthFebruary",
  "common.monthMarch",
  "common.monthApril",
  "common.monthMay",
  "common.monthJune",
  "common.monthJuly",
  "common.monthAugust",
  "common.monthSeptember",
  "common.monthOctober",
  "common.monthNovember",
  "common.monthDecember",
] as const;

function ignoreHistoryPaymentSelect(_paymentId: string): void {
  // History payments are not selectable.
}

function scrollPaymentItemIntoView(
  item: View | null,
  scrollView: ScrollView | null,
): void {
  if (!item || !scrollView) {
    return;
  }
  item.measureLayout(
    scrollView as unknown as Parameters<View["measureLayout"]>[0],
    (_x: number, y: number) => {
      scrollView.scrollTo({
        y: Math.max(0, y - 100),
        animated: true,
      });
    },
    () => {
      // Fallback: if measureLayout fails, do nothing
    },
  );
}

interface PaymentDetailsData {
    id: string;
    title: string;
    amount: number;
    dueDate: string;
  status: "paid" | "missed" | "due-soon" | "in-process" | "future";
    propertyName: string;
    /** Profile unit that owns this installment (same row as propertyName). */
    isOnlinePaymentEnabled?: boolean;
    paymentDate?: string; // For history payments (formatted as DD/MM/YYYY)
}

export default function PropertyPaymentsScreen() {
    const colors = useUIColors();
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const params = useLocalSearchParams<{
    tab?: string;
    selectedPaymentIds?: string;
    selectedPropertyIds?: string;
  }>();
    const scrollViewRef = useRef<ScrollView>(null);
    const paymentItemRefs = useRef<Record<string, View | null>>({});

    // Get data from store (needed for initialization)
    const properties = useAppStore((state) => state.properties);
    const isAdhaJourney = useAppStore((state) => state.isAdhaJourney);

    // Initialize tab from URL params, default to 'upcoming'
  const [activeTab, setActiveTab] = useState<TabType>(
    (params.tab as TabType) || "upcoming",
  );

    // Track if we came from a previous screen with pre-selected items
    const cameFromPreviousScreen = useRef<boolean>(!!params.selectedPaymentIds);

    // Initialize selected payments from URL params
    const [selectedPayments, setSelectedPayments] = useState<string[]>(() => {
        if (params.selectedPaymentIds) {
      return params.selectedPaymentIds.split(",").filter(Boolean);
        }
        return [];
    });

    // Get filter state from Zustand store
  const selectedPropertyIds = usePaymentFilterStore(
    (state) => state.selectedPropertyIds,
  );
  const setSelectedPropertyIds = usePaymentFilterStore(
    (state) => state.setSelectedPropertyIds,
  );

  useEffect(() => {
    trackDynatraceEvent({
      p_screen: DT_SCREEN.PROPERTY_PAYMENTS,
      p_actionName: "payments_page_viewed",
      eventType: "view",
    });
  }, []);

    // Initialize from URL params on mount (only once)
    useEffect(() => {
    if (
      params.selectedPropertyIds &&
      params.selectedPropertyIds.trim() !== ""
    ) {
      const ids = params.selectedPropertyIds.split(",").filter(Boolean);
            setSelectedPropertyIds(ids);
        } else {
            // If no params, no filters selected (show all)
            setSelectedPropertyIds([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    // Update tab when URL param changes
    useEffect(() => {
    if (params.tab && (params.tab === "upcoming" || params.tab === "history")) {
            setActiveTab(params.tab as TabType);
        }
    }, [params.tab]);

    // Update selected payments when URL param changes (only from navigation, not manual selection)
    useEffect(() => {
        if (params.selectedPaymentIds) {
      const ids = params.selectedPaymentIds.split(",").filter(Boolean);
            setSelectedPayments(ids);
            // Mark that we came from navigation if URL param is present
            cameFromPreviousScreen.current = true;
        } else {
            // Only clear selection if param is removed AND we came from navigation
            // Don't clear if user manually deselected items
            if (cameFromPreviousScreen.current) {
                setSelectedPayments([]);
                cameFromPreviousScreen.current = false;
            }
        }
    }, [params.selectedPaymentIds]);

    // Note: Filter state is now managed by Zustand store, not URL params
    // URL params are only used for initial load

    // Get data from store
    const unitInstallments = useAppStore((state) => state.unitInstallments);

    // Helper function to translate payment type
  const translatePaymentType = useCallback(
    (type: string): string => {
      if (type === null || type === undefined) return "";
      if (typeof type !== "string") return type as any;
        const typeLower = type.toLowerCase();
      if (typeLower.includes("handover")) return t("propertyDetails.handover");
      if (typeLower.includes("installment"))
        return t("propertyDetails.installment");
      if (typeLower.includes("down payment"))
        return t("propertyDetails.downPayment");
      if (typeLower.includes("adm fee") || typeLower.includes("adm"))
        return t("propertyDetails.admFee");
        return type; // Return original if no match
    },
    [t],
  );

    // Filter properties based on selected property IDs
    // If no filters selected, show all properties (no filter = show all)
    const filteredProperties = useMemo(() => {
        if (selectedPropertyIds.length === 0) {
            return properties; // No filter = show all
        }
    return properties.filter((p) => selectedPropertyIds.includes(p.id));
    }, [properties, selectedPropertyIds]);

    // Transform installments to PaymentDetailsData format for upcoming tab
    const upcomingPayments = useMemo((): PaymentDetailsData[] => {
    const overdueItems = getAllOverdueInstallments(
      unitInstallments,
      filteredProperties,
    );
    const dueSoonItems = getAllUpcomingDueInstallments(
      unitInstallments,
      filteredProperties,
    );

        // Get future installments (these are upcoming, not in-process)
        const futureItems: {
            id: string;
            propertyId: string;
            propertyName: string;
            isOnlinePaymentEnabled: boolean;
            due_date: string;
            due_amount: number;
            paid_amount: number | null;
            type: string;
            payment_name: string;
      category: "future";
        }[] = [];

        filteredProperties.forEach((property) => {
            const installments = unitInstallments[property.id];
            if (installments?.future) {
                installments.future.forEach((item) => {
                    futureItems.push({
                        ...item,
                        propertyId: property.id,
            propertyName:
              property.unitNumber || property.projectName || "Unknown Property",
            isOnlinePaymentEnabled: property.isOnlinePaymentEnabled === true,
            category: "future" as const,
                    });
                });
            }
        });

        const allItems = [
      ...overdueItems.map((item) => ({
        ...item,
        category: "overdue" as const,
      })),
      ...dueSoonItems.map((item) => ({
        ...item,
        category: "upcomingDue" as const,
      })),
      ...futureItems.map((item) => ({ ...item, category: "future" as const })),
        ];

        return allItems.map((item) => {
            // Map status based on category (future items are 'due-soon', not 'in-process')
            // For future items, use 'upcoming' as status string, for others use item.status
      const statusString =
        item.category === "future"
          ? "upcoming"
          : (item as any).status || "upcoming";
      const status = mapInstallmentStatusToPaymentStatus(
        item.category,
        statusString,
      );

            return {
                id: item.id,
                title: translatePaymentType(item.type || item.payment_name),
                // For items that need payment (overdue, upcoming, future), show due_amount
                amount: item.due_amount || 0,
        dueDate: formatDueDateWithPrefix(
          item.due_date,
          t("propertyDetails.dueBy"),
        ),
                status,
                propertyName: item.propertyName,
                isOnlinePaymentEnabled: item.isOnlinePaymentEnabled === true,
            };
        });
    }, [unitInstallments, filteredProperties, t, translatePaymentType]);

    // Transform paid installments to PaymentDetailsData format for history tab
    const historyPayments = useMemo(() => {
        // Get all paid installments without limit for history
        const paidItems: {
            id: string;
            propertyId: string;
            propertyName: string;
            due_date: string;
            due_amount: number;
            paid_amount: number | null;
            amount: number;
            type: string;
            payment_name: string;
            status: string;
      category: "paid";
        }[] = [];

        filteredProperties.forEach((property) => {
            const installments = unitInstallments[property.id];
            if (installments?.paid) {
                installments.paid.forEach((item) => {
                    paidItems.push({
                        ...item,
                        propertyId: property.id,
            propertyName:
              property.unitNumber || property.projectName || "Unknown Property",
            category: "paid" as const,
                    });
                });
            }
        });

        // Sort by due_date descending (most recent first)
    const sortedPaidItems = paidItems.sort(
      (a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime(),
        );

        return sortedPaidItems.map((item) => {
            // Determine if payment is in-process (paid_amount < due_amount)
      const isInProcess =
        item.paid_amount !== null &&
        item.due_amount > 0 &&
        item.paid_amount < item.due_amount;
      const status = isInProcess
        ? "in-process"
        : mapInstallmentStatusToPaymentStatus(item.category, item.status);

            return {
                id: item.id,
                title: translatePaymentType(item.type || item.payment_name),
                amount: item.paid_amount || item.amount,
                // Backend only provides due date, so use \"Due date\" label consistently for history as well
        dueDate: `${t("payments.dueDate" as const)} ${formatDueDate(item.due_date)}`,
                status,
                propertyName: item.propertyName,
                paymentDate: formatDueDate(item.due_date), // For grouping by year/month
            };
        });
    }, [unitInstallments, filteredProperties, t, translatePaymentType]);

    // Track if we've scrolled to first selected item (to prevent multiple scrolls)
    // Only scroll if we came from a previous screen with pre-selected items
    const hasScrolledToFirstSelected = useRef(false);

    // Scroll to first selected item when screen loads with pre-selected items from navigation
    useEffect(() => {
        // Only enable auto-scroll if:
        // 1. We came from a previous screen (cameFromPreviousScreen.current === true)
        // 2. There are selected payments
        // 3. We're on the upcoming tab
        // 4. We haven't scrolled yet
        if (
            cameFromPreviousScreen.current &&
            selectedPayments.length > 0 &&
      activeTab === "upcoming" &&
            !hasScrolledToFirstSelected.current
        ) {
            // Mark that we should scroll (actual scroll happens in onLayout)
            // This flag will be set to false after the scroll completes
            hasScrolledToFirstSelected.current = true;
        }

        // If user manually changes selection, disable auto-scroll
        if (!cameFromPreviousScreen.current) {
            hasScrolledToFirstSelected.current = false;
        }
    }, [selectedPayments, activeTab]);

    const canSelectPayment = (payment: PaymentDetailsData) =>
        isAdhaJourney || payment.isOnlinePaymentEnabled === true;

    useEffect(() => {
        if (isAdhaJourney) return;
        if (upcomingPayments.length === 0) return;
        setSelectedPayments((prev) =>
            prev.filter((id) =>
                upcomingPayments.some(
                    (payment) => payment.id === id && payment.isOnlinePaymentEnabled === true,
                ),
            ),
        );
    }, [isAdhaJourney, upcomingPayments]);

    const handlePaymentSelect = (paymentId: string) => {
        const payment = upcomingPayments.find((item) => item.id === paymentId);
        if (payment && !canSelectPayment(payment)) return;
        // Mark that we're now manually selecting (not from navigation)
        cameFromPreviousScreen.current = false;
    setSelectedPayments((prev) =>
            prev.includes(paymentId)
        ? prev.filter((id) => id !== paymentId)
        : [...prev, paymentId],
        );
    };

    const handleSectionSelect = (paymentIds: string[]) => {
        // Mark that we're now manually selecting (not from navigation)
        cameFromPreviousScreen.current = false;
    const allSelected = paymentIds.every((id) => selectedPayments.includes(id));

    trackDynatraceEvent({
      p_screen: "Property Payments",
      p_actionName: allSelected
        ? "Payment Section Deselected"
        : "Payment Section Selected",
      p_sectionName: "Payment List",
      eventType: "action",
      paymentCount: paymentIds.length,
    });
        if (allSelected) {
            // Deselect all payments in this section
      setSelectedPayments((prev) =>
        prev.filter((id) => !paymentIds.includes(id)),
      );
        } else {
            // Select all payments in this section
      setSelectedPayments((prev) => [...new Set([...prev, ...paymentIds])]);
        }
    };

    const handleCancelSelection = () => {
        // Mark that we're now manually deselecting (not from navigation)
        cameFromPreviousScreen.current = false;
        setSelectedPayments([]);
    };

    const handlePaymentSummary = () => {
    trackCatalogEvent({
      screen: DT_SCREEN.PROPERTY_PAYMENTS,
      actionName: "property_payments_pay_now",
      eventType: "action",
    });
    router.push(
      `/(auth)/payment-summary?selectedPaymentIds=${selectedPayments.join(",")}` as any,
    );
    };

    const getTotalAmount = () => {
        return selectedPayments.reduce((total, paymentId) => {
      const payment = upcomingPayments.find((p) => p.id === paymentId);
            return total + (payment?.amount || 0);
        }, 0);
    };

    const handleFilterPress = () => {
        // Navigate to filter screen
        // Filter screen will read from Zustand store
    router.push("/(auth)/payment-filter" as any);
    };

    // Get filter title based on selected properties
    const filterTitle = useMemo(() => {
        if (selectedPropertyIds.length === 0) {
      return t("payments.allProperties");
        } else if (selectedPropertyIds.length === 1) {
      const property = properties.find((p) => p.id === selectedPropertyIds[0]);
      return (
        property?.unitNumber ||
        property?.projectName ||
        t("payments.allProperties")
      );
        } else {
      return `${selectedPropertyIds.length} ${t("payments.propertiesSelected")}`;
        }
    }, [selectedPropertyIds, properties, t]);

    // Adjust selected payments when filter changes - remove payments that are no longer visible
    useEffect(() => {
        // Get all visible payment IDs based on current filter
        const visiblePaymentIds = new Set<string>();

        // Add upcoming payment IDs
    upcomingPayments.forEach((p) => visiblePaymentIds.add(p.id));

        // Add history payment IDs
    historyPayments.forEach((p) => visiblePaymentIds.add(p.id));

        // Remove selected payments that are no longer visible
    setSelectedPayments((prev) =>
      prev.filter((id) => visiblePaymentIds.has(id)),
    );
    }, [filteredProperties, upcomingPayments, historyPayments]);

    // Group upcoming payments by status
  const missedPayments = useMemo(
    () => upcomingPayments.filter((p) => p.status === "missed"),
    [upcomingPayments],
    );
  const dueSoonPayments = useMemo(
    () => upcomingPayments.filter((p) => p.status === "due-soon"),
    [upcomingPayments],
    );
  const futurePayments = useMemo(
    () => upcomingPayments.filter((p) => p.status === "future"),
    [upcomingPayments],
    );

    // Get counts for actual "upcomingDue" vs "future" payments
    // We need to check the original category from the installments data
    const upcomingDueCount = useMemo(() => {
        let count = 0;
        filteredProperties.forEach((property) => {
            const installments = unitInstallments[property.id];
            if (installments?.upcomingDue) {
                count += installments.upcomingDue.length;
            }
        });
        return count;
    }, [unitInstallments, filteredProperties]);

    // Calculate badge count and color based on priority using utility function
    const badgeInfo = useMemo(() => {
        return getUpcomingTabBadgeInfo(
            missedPayments.length,
            upcomingDueCount,
            colors.bloomRed,
      UI_PRIMITIVES.yellowDark,
        );
    }, [missedPayments.length, upcomingDueCount, colors.bloomRed]);

    // Format badge count to be responsive and capped to 3 characters (e.g. 99+)
    const formattedBadgeCount = useMemo(() => {
    if (!badgeInfo.show) return "";

        const countNumber = Number(badgeInfo.count);
        if (Number.isNaN(countNumber)) {
      return String(badgeInfo.count ?? "");
        }

        return String(countNumber);
    }, [badgeInfo.show, badgeInfo.count]);

    // Badge shape: circle for up to 2 characters, pill for 3+
  const isWideBadge = useMemo(
    () => formattedBadgeCount.length > 2,
    [formattedBadgeCount],
  );

    // Group history payments by year and month
    const groupedHistoryPayments = useMemo(() => {
    return historyPayments.reduce(
      (groups, payment) => {
            if (!payment.paymentDate) return groups;

            // Parse date from DD/MM/YYYY format
        const dateParts = payment.paymentDate.split("/");
            if (dateParts.length !== 3) return groups;

            const day = dateParts[0];
            const month = dateParts[1];
            const year = dateParts[2];

            if (!day || !month || !year) return groups;

        const date = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
        );
            if (isNaN(date.getTime())) return groups;

            const yearStr = date.getFullYear().toString();
            const monthIndex = date.getMonth();
            const monthKey = MONTH_KEYS[monthIndex] ?? MONTH_KEYS[0];
            const monthStr = t(monthKey);

            let yearGroup = groups[yearStr];
            if (!yearGroup) {
              yearGroup = {};
              groups[yearStr] = yearGroup;
            }
            let monthList = yearGroup[monthStr];
            if (!monthList) {
              monthList = [];
              yearGroup[monthStr] = monthList;
            }
            monthList.push(payment);
            return groups;
      },
      {} as Record<string, Record<string, PaymentDetailsData[]>>,
    );
    }, [historyPayments, t]);

    const renderPaymentSection = (
        title: string,
        payments: PaymentDetailsData[],
        icon: React.ReactNode,
    count: number,
    ) => {
        if (payments.length === 0) return null;

    const paymentIds = payments
        .filter((payment) => canSelectPayment(payment))
        .map((p) => p.id);
    const allSelected =
        paymentIds.length > 0 && paymentIds.every((id) => selectedPayments.includes(id));

        return (
            <View style={styles.paymentSection}>
                <TouchableOpacity
                    style={styles.sectionHeader}
                    onPress={() => {
                        if (paymentIds.length === 0) return;
                        handleSectionSelect(paymentIds);
                    }}
                    disabled={paymentIds.length === 0}
                >
                    {paymentIds.length > 0 && (
                    <Checkbox
                        isSelected={allSelected}
                        onPress={() => handleSectionSelect(paymentIds)}
                        size={19}
                    />
                    )}
                    <View style={styles.sectionTitleContainer}>
                        {icon}
            <Typography
              variant="headline"
              color="primary"
              style={styles.sectionTitle}
            >
                            {isRTL ? `${count} : ${title}` : `${count} ${title}`}
                        </Typography>
                    </View>
                </TouchableOpacity>

                <View style={styles.paymentsList}>
                    {payments.map((payment) => (
                        <View
                            key={payment.id}
                            ref={(ref) => {
                                paymentItemRefs.current[payment.id] = ref;
                            }}
                            onLayout={() => {
                                // Only trigger scroll if:
                                // 1. We came from a previous screen (cameFromPreviousScreen.current === true)
                                // 2. This is the first selected item
                                // 3. We're on the upcoming tab
                                // 4. We haven't scrolled yet (hasScrolledToFirstSelected.current === true)
                                if (
                                    cameFromPreviousScreen.current &&
                                    selectedPayments.length > 0 &&
                                    payment.id === selectedPayments[0] &&
                  activeTab === "upcoming" &&
                                    hasScrolledToFirstSelected.current
                                ) {
                                    // Mark that we've scrolled to prevent future scrolls
                                    hasScrolledToFirstSelected.current = false;

                                    setTimeout(() => {
                                        scrollPaymentItemIntoView(
                                            paymentItemRefs.current[payment.id] ?? null,
                                            scrollViewRef.current,
                                        );
                                    }, 100);
                                }
                            }}
                        >
                            <PaymentItemMobile      
                                id={payment.id}
                                title={payment.title}
                                amount={payment.amount}
                                dueDate={payment.dueDate}
                                status={payment.status}
                                propertyName={payment.propertyName}
                                onlinePaymentEnabled={canSelectPayment(payment)}
                                isSelected={selectedPayments.includes(payment.id)}
                                {...(canSelectPayment(payment)
                                    ? { onSelect: handlePaymentSelect }
                                    : {})}
                            />
                        </View>
                    ))}
                </View>
            </View>
        );
    };

    const renderHistoryPayments = () => {
        // Check if history is empty
        if (historyPayments.length === 0) {
            return <PaymentsEmptyState variant="history" />;
        }

        return Object.entries(groupedHistoryPayments).map(([year, months]) => (
            <View key={year} style={styles.historyYearSection}>
                <Typography variant="headline" color="primary" style={[styles.yearTitle, isRTL && styles.historyDateRTL]}>
                    {year}
                </Typography>
                {Object.entries(months).map(([month, payments]) => (
                    <View key={`${year}-${month}`} style={styles.historyMonthSection}>
            <Typography
              variant="subhead"
              color="primary"
              style={[styles.monthTitle, isRTL && styles.historyDateRTL]}
            >
                            {month}
                        </Typography>
                        <View style={styles.historyPaymentsList}>
                            {payments.map((payment) => (
                                <PaymentItemMobile  
                                    key={payment.id}
                                    id={payment.id}
                                    title={payment.title}
                                    amount={payment.amount}
                                    dueDate={payment.dueDate}
                                    status={payment.status}
                                    propertyName={payment.propertyName}
                                    isSelected={false}
                  onSelect={ignoreHistoryPaymentSelect}
                                />
                            ))}
                        </View>
                    </View>
                ))}
            </View>
        ));
    };

    return (
    <View
      style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <TextHeader title={t("payments.propertyPayments")} />

            <ScrollView
                ref={scrollViewRef}
                contentContainerStyle={[
                    styles.scrollContent,
          {
            paddingBottom:
              selectedPayments.length > 0 ? 150 : insets.bottom + 24,
          },
                ]}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.content}>
                    {/* Tab Navigation */}
                    <View style={styles.tabContainer}>
                        <TouchableOpacity
                            style={styles.tab}
              onPress={() => {
                trackCatalogEvent({
                  screen: DT_SCREEN.PROPERTY_PAYMENTS,
                  actionName: "property_payments_tab_switch",
                  eventType: "action",
                  properties: { tab: "upcoming" },
                });
                setActiveTab("upcoming");
              }}
                        >
                            <View style={styles.tabContent}>
                                <View style={styles.tabTitleContainer}>
                                    <Typography
                                        variant="headline"
                    color={activeTab === "upcoming" ? "primary" : "secondary"}
                                        style={styles.tabTitle}
                                    >
                    {t("payments.upcoming")}
                                    </Typography>
                                    {badgeInfo.show && (
                                        <View
                                            style={[
                                                styles.badge,
                                                isWideBadge && styles.badgeWide,
                                                { backgroundColor: badgeInfo.color },
                                            ]}
                                        >
                                            <Typography
                                                variant="footnote"
                                                color="white"
                                                style={styles.badgeText}
                                                numberOfLines={1}
                                            >
                                                {formattedBadgeCount}
                                            </Typography>
                                        </View>
                                    )}
                                </View>
                <View
                  style={[
                    styles.tabIndicator,
                    {
                      backgroundColor:
                        activeTab === "upcoming"
                          ? colors.fontPrimary
                          : "#ededed",
                      height: activeTab === "upcoming" ? 4 : 1,
                    },
                  ]}
                />
                            </View>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.tab}
              onPress={() => setActiveTab("history")}
                        >
                            <View style={styles.tabContent}>
                                <Typography
                                    variant="headline"
                  color={activeTab === "history" ? "primary" : "secondary"}
                                    style={styles.tabTitle}
                                >
                  {t("payments.history")}
                                </Typography>
                <View
                  style={[
                    styles.tabIndicator,
                    {
                      backgroundColor:
                        activeTab === "history"
                          ? colors.fontPrimary
                          : "#ededed",
                      height: activeTab === "history" ? 4 : 1,
                    },
                  ]}
                />
                            </View>
                        </TouchableOpacity>
                    </View>

                    {/* Filter Section */}
                    <View style={styles.filterSection}>
                        <View style={styles.filterTitleContainer}>
              <Typography
                variant="title3"
                color="primary"
                style={styles.filterTitle}
              >
                                {filterTitle}
                            </Typography>
                        </View>
                        <Button
              title={t("payments.filter")}
                            variant="text"
                            onPress={handleFilterPress}
                            leading={<FunnelIcon size={18} color={colors.fontPrimary} />}
                        />
                    </View>

                    {/* Payment Sections */}
          {activeTab === "upcoming" && (
                        <View style={styles.paymentsContainer}>
                            {upcomingPayments.length === 0 ? (
                                <PaymentsEmptyState type="property" />
                            ) : (
                                <>
                                    {renderPaymentSection(
                    t("payments.missedPayments"),
                                        missedPayments,
                    <TriangleAlertIcon
                      size={20}
                      color={colors.fontErrorText}
                    />,
                    missedPayments.length,
                                    )}

                                    {renderPaymentSection(
                    t("payments.dueSoonPayments"),
                                        dueSoonPayments,
                    <TimerIconBold
                      size={20}
                      color={UI_PRIMITIVES.yellowDark}
                    />,
                    dueSoonPayments.length,
                                    )}

                                    {renderPaymentSection(
                    t("payments.futurePayments"),
                                        futurePayments,
                                        <CalendarIcon size={20} />,
                    futurePayments.length,
                                    )}
                                </>
                            )}
                        </View>
                    )}

          {activeTab === "history" && (
                        <View style={styles.paymentsContainer}>
                            {renderHistoryPayments()}
                        </View>
                    )}
                </View>
            </ScrollView>

            {/* Bottom Action Panel - For upcoming tab with selected payments */}
            {/* NOTE: Increased vertical spacing in summary section per marketing team request.
                 This differs from the original Figma design but improves readability. */}
      {activeTab === "upcoming" && selectedPayments.length > 0 && (
        <View
          style={[
            styles.bottomPanel,
            {
              backgroundColor: colors.backgroundLevel1,
              paddingBottom: insets.bottom + 16,
            },
          ]}
        >
                    <View style={styles.summarySection}>
                        <View style={styles.summaryLeft}>
              <Typography
                variant="subhead"
                color="secondary"
                style={styles.summaryLabel}
              >
                {t("payments.total")}
                            </Typography>
              <Typography
                variant="body"
                color="primary"
                style={styles.summaryCount}
              >
                {isRTL
                  ? `${selectedPayments.length === 1 ? t("payments.paymentSelected") : t("payments.paymentsSelected")} : ${selectedPayments.length}`
                  : `${selectedPayments.length} ${selectedPayments.length === 1 ? t("payments.paymentSelected") : t("payments.paymentsSelected")}`}
                            </Typography>
                        </View>
                        <View style={styles.summaryRight}>
              <View style={[styles.amountContainer, { direction: "ltr" }]}>
                                <DirhamIcon size={18} color={colors.fontPrimary} />
                <Typography
                  variant="title2"
                  color="primary"
                  style={styles.totalAmount}
                >
                                    {getTotalAmount().toLocaleString()}
                                </Typography>
                            </View>
              <Typography
                variant="caption"
                color="secondary"
                style={styles.taxNote}
              >
                {t("payments.excludingTaxes")}
                            </Typography>
                        </View>
                    </View>

                    <View style={styles.actionButtons}>
                        <Button
              title={t("payments.cancel")}
                            variant="outline"
                            onPress={handleCancelSelection}
                            style={styles.cancelButton}
                        />
                        <Button
              title={t("payments.paymentSummary")}
                            variant="primary"
                            onPress={handlePaymentSummary}
                            style={styles.summaryButton}
                        />
                    </View>
                </View>
            )}

            {/* Bottom Action Panel - For history tab when empty (Account statement button only) */}
      {activeTab === "history" && historyPayments.length === 0 && (
        <View
          style={[
            styles.bottomPanel,
            {
              backgroundColor: colors.backgroundLevel1,
              paddingBottom: insets.bottom + 16,
            },
          ]}
        >
                    {/* <Button
            title={t("payments.accountStatement")}
                        variant="outline"
            onPress={() =>
              logger.debug("PropertyPaymentsScreen", "Account statement")
            }
                        style={styles.accountStatementButton}
                    /> */}
                </View>
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollContent: {
        paddingBottom: 24,
    },
    content: {
        paddingHorizontal: 16,
        paddingTop: 8,
        gap: 16,
    },
    tabContainer: {
    flexDirection: "row",
    },
    tab: {
        flex: 1,
        paddingTop: 8,
        paddingBottom: 0,
        paddingHorizontal: 10,
    },
    tabContent: {
    alignItems: "center",
        gap: 0,
    },
    tabTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
        gap: 4,
    },
    tabTitle: {
        fontSize: 16,
        paddingBottom: 8,
    fontWeight: "600",
    },
    badge: {
        width: 18,
        height: 18,
        borderRadius: 9,
    justifyContent: "center",
    alignItems: "center",
        marginBottom: 6,
    },
    badgeWide: {
        minWidth: 28,
        paddingHorizontal: 6,
    },
    badgeText: {
        fontSize: 11,
    fontWeight: "600",
    color: "white",
    textAlign: "center",
        includeFontPadding: false,
    },
    tabIndicator: {
        height: 4,
    width: "100%",
    },
    filterSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
        paddingVertical: 8,
    },
    filterTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    },
    filterTitle: {
        fontSize: 20,
    fontWeight: "500",
    },
    paymentsContainer: {
        gap: 12,
    },
    paymentSection: {
        gap: 12,
    },
    sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
        gap: 12,
        paddingVertical: 8,
    },
    sectionTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
        gap: 4,
    },
    sectionTitle: {
        fontSize: 16,
    fontWeight: "600",
        paddingTop: 4,
    },
    paymentsList: {
        gap: 12,
    },
    emptyState: {
    textAlign: "center",
        paddingVertical: 32,
    },
    bottomPanel: {
    position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
    shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 0,
        },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
    },
    summarySection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
        marginBottom: 12, // Increased from 8px per marketing team request
    },
    summaryLeft: {
        flex: 1,
    },
    summaryLabel: {
        fontSize: 14,
    fontWeight: "500",
        marginBottom: 8, // Increased from 4px per marketing team request (doubled for better spacing)
    },
    summaryCount: {
        fontSize: 16,
    fontWeight: "400",
    },
    summaryRight: {
    alignItems: "flex-end",
    },
    amountContainer: {
    flexDirection: "row",
    alignItems: "center",
        gap: 2,
        marginBottom: 8, // Increased from 4px per marketing team request (doubled for better spacing)
    },
    totalAmount: {
        fontSize: 22,
    fontWeight: "500",
    },
    taxNote: {
        fontSize: 12,
    fontWeight: "400",
    },
    actionButtons: {
    flexDirection: "row",
        gap: 24,
    alignItems: "center",
    },
    cancelButton: {
        flex: 1,
        height: 48, // Fixed height to match primary button (accounts for 2px border on outline variant)
    },
    summaryButton: {
        flex: 2,
        height: 48, // Fixed height to match cancel button
    },
    accountStatementButton: {
    width: "100%",
    },
    historyYearSection: {
        marginBottom: 16,
    },
    yearTitle: {
        fontSize: 16,
    fontWeight: "600",
        marginBottom: 8,
    },
    historyMonthSection: {
        marginBottom: 12,
    },
    monthTitle: {
        fontSize: 14,
    fontWeight: "500",
        marginBottom: 8,
    },
    historyDateRTL: {
        textAlign: "right",
    },
    historyPaymentsList: {
        gap: 12,
    },
});
